import React from "react";
import { Box, List, ListItem, ListItemIcon, Paper } from "@material-ui/core";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import { makeStyles } from "@material-ui/core/styles";
import { useMachine } from "@xstate/react";
import clsx from "clsx";

import { Logo360 } from "@eroad/icons/svg";

import type { LogoProps } from "../../Logo";
import { Logo } from "../../Logo";
import navMachine from "../../statemachines/NavMachine";
import type { CoretexTheme } from "../../theme";
import MenuListItem from "./MenuListItem";
import type { MenuItem } from "./types";

const drawerWidth = 250;
const useStyles = makeStyles((theme: CoretexTheme) => ({
  baseBox: {
    position: "relative",
    left: 0,
    minHeight: "100vh",
    maxHeight: "100vh",
  },
  boxOverlay: {
    position: "absolute!important" as "absolute",
    left: 0,
    zIndex: 4,
  },
  drawerPaper: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    whiteSpace: "nowrap",
    width: drawerWidth,
    minHeight: "100vh",
    maxHeight: "100vh",
    overflow: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    backgroundColor: theme.palette.mono.superDark,
    color: theme.palette.mono.white,
  },
  drawerPaperClose: {
    overflow: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(14),
    minHeight: "100vh",
    maxHeight: "100vh",
  },
  logo: {
    position: "fixed",
    width: "56px",
    top: "0px",
    paddingLeft: theme.spacing(3),
    paddingTop: "16px",
    height: "64px",
  },
  bgNone: {
    "&:hover, &.Mui-focusVisible": { backgroundColor: "transparent" },
  },
  railIcon: {
    "&:hover": {
      backgroundColor: `${theme.palette.mono.ultraDark} !important`,
    },
    justifyContent: "center",
    minHeight: "56px",
  },
  backdrop: {
    zIndex: 1,
  },
  navList: {
    overflowY: "auto",
    overflowX: "hidden",
    "&::-webkit-scrollbar": {
      width: "8px",
      backgroundColor: "#F5F5F5",
    },
    "&::-webkit-scrollbar-track": {
      borderRadius: "4px",
      backgroundColor: theme.palette.mono.white,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#BBBBBB",
      borderRadius: "4px",
    },
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flexGrow: 1,
  },
  icon: {
    minWidth: 0,
    "& > *": {
      "& > *": {
        fill: theme.palette.mono.secondaryDark,
      },
    },
  },
  selected: {
    minWidth: 0,
    "& > *": {
      "& > *": {
        fill: theme.palette.mono.white,
      },
    },
  },
  active: {
    position: "relative",
    backgroundColor: `${theme.palette.mono.ultraDark} !important`,
    "&:hover": { backgroundColor: theme.palette.mono.ultraDark },
    "&::before": {
      display: "block",
      position: "absolute",
      content: '""',
      top: 0,
      left: 0,
      backgroundColor: "#E75E1F",
      height: "100%",
      width: "4px",
    },
  },
  divider: {
    backgroundColor: theme.palette.mono.white,
  },
  list: {
    display: "flex",
  },
}));

export interface NavMenuProps {
  /** Navigation tiers */
  menuItems?: MenuItem[];
  /** Application actions e.g. User Profile */
  actionItems?: MenuItem[];
  /** Selected menu item, could be submenu item */
  selected: string;
  /** The list of menu items expanded */
  expandedItems?: string[];
  onChangeState: (state: string) => void;
}

/**
  NavMenu - common navigation
*/
export const NavMenu = ({
  menuItems,
  actionItems,
  selected,
  expandedItems,
  onChangeState,
}: NavMenuProps): JSX.Element => {
  const classes = useStyles();
  const [current, send] = useMachine(navMachine);
  const [expanded, setExpanded] = React.useState<string[]>(expandedItems ?? []);

  const ListItemLink = (props: any): React.ReactElement => <ListItem {...props} />;

  const setExpand = (item: string) => {
    const exists = expanded.indexOf(item) >= 0;
    if (exists) {
      setExpanded((prev) => prev.filter((p) => p !== item));
    } else {
      setExpanded((prev) => [...prev, item]);
    }
  };

  const iconList = (menuItem: MenuItem): React.ReactElement => {
    const isSelected = selected === menuItem.text;
    const { text, url, ...rest } = menuItem;
    return (
      <React.Fragment key={`lst-${url ?? text}`}>
        <ListItemLink
          data-testid={`nav-${text}`}
          key={`icn-${url ?? text}`}
          button
          selected={isSelected}
          className={clsx(classes.railIcon, isSelected && classes.active)}
          onMouseEnter={() => send({ type: "HOVER" })}
          href={url}
          to={url}
          {...rest}
        >
          <ListItemIcon key={`li-${menuItem.text}`} className={isSelected ? classes.selected : classes.icon}>
            {menuItem.icon}
          </ListItemIcon>
        </ListItemLink>
      </React.Fragment>
    );
  };

  const logoSet: LogoProps = {
    height: 32,
    width: 150,
  };
  const logoSetMini: LogoProps = {
    height: 32,
    width: 32,
  };
  React.useEffect(() => {
    if (onChangeState) onChangeState(current.value as string);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  if (current.matches("rail")) {
    return (
      <>
        <Paper
          elevation={1}
          square
          id="navMenu"
          data-testid="navMenu"
          onMouseEnter={() => send({ type: "HOVER" })}
          className={clsx(classes.drawerPaper, classes.drawerPaperClose)}
        >
          <div className={classes.logo}>
            <Logo {...logoSetMini} mini />
          </div>
          <div style={{ height: "88px" }} />
          <div className={classes.navList}>
            <List key="menu-items">{menuItems && menuItems.map((menuItem: MenuItem) => iconList(menuItem))}</List>
            <List key="action-items">
              {actionItems && actionItems.map((actionItem: MenuItem) => iconList(actionItem))}
            </List>
          </div>
        </Paper>
      </>
    );
  }
  if (current.matches("open")) {
    return (
      <>
        <ClickAwayListener onClickAway={() => send({ type: "UNHOVER" })}>
          <Paper
            id="navMenu"
            elevation={16}
            square
            onMouseLeave={() => send({ type: "UNHOVER" })}
            onClick={() => send({ type: "UNHOVER" })}
            className={clsx(classes.drawerPaper, classes.boxOverlay)}
          >
            <Box component="div" display="fixed" top={0}>
              <Box display="flex" flexWrap="nowrap" width={drawerWidth} height={88}>
                <Box id="logo-container" flexGrow={3} pt={4} pl={3}>
                  <Logo360 {...logoSet} />
                </Box>
              </Box>
            </Box>
            <div className={classes.navList}>
              <List>
                {menuItems &&
                  menuItems.map((menuItem: MenuItem) => (
                    <MenuListItem
                      key={`open-${menuItem.url}-${menuItem.text}`}
                      menuItem={menuItem}
                      menuLevel={1}
                      expanded={expanded}
                      setExpanded={setExpand}
                      selected={selected === menuItem.text}
                      offset={drawerWidth}
                    />
                  ))}
              </List>
              <List>
                {actionItems &&
                  actionItems.map((actionItem: MenuItem) => (
                    <MenuListItem
                      key={`open-action-${actionItem.url}-${actionItem.text}`}
                      menuItem={actionItem}
                      menuLevel={1}
                      expanded={expanded}
                      setExpanded={setExpand}
                      selected={false}
                      offset={drawerWidth}
                      bottomSubMenu
                    />
                  ))}
              </List>
            </div>
          </Paper>
        </ClickAwayListener>
      </>
    );
  }
  return <></>;
};
